import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageHeader, DataTable, Td, EmptyState, FormDialog, Field, TextInput, ActionButton } from "../../components/common";

const STATUS_STYLES = {
  available: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  deployed: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  maintenance: "bg-amber-500/10 text-amber-400 border-amber-500/30",
};

const INVENTORY_COLUMNS = ["Tank/Asset #", "Type", "Owned By", "Status", "Job #", "Current Location", ""];

const InvBadge = ({ status, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs font-mono uppercase tracking-wider ${STATUS_STYLES[status] || STATUS_STYLES.available}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current" />
    {status}
  </span>
);

export default function FracInventory() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/fractank/inventory").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      asset_name: a.asset_name || "",
      asset_type: a.asset_type || "",
      description: a.description || "",
      owned_by: a.owned_by || "",
    });
  };

  const save = async () => {
    if (!form.asset_name.trim()) {
      toast.error("Tank/Asset # is required");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/assets/${editing.id}`, {
        asset_name: form.asset_name.trim(),
        asset_type: form.asset_type,
        description: form.description,
        owned_by: form.owned_by,
      });
      toast.success(`${form.asset_name} updated`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="frac-inventory-page">
      <PageHeader title="Rentals Quick View" subtitle="Rental equipment, current location and availability at a glance." testId="frac-inventory-header" />

      {items.length === 0 ? (
        <EmptyState message="No rental assets in the fleet" testId="frac-inventory-empty" />
      ) : (
        <DataTable
          testId="frac-inventory-table"
          columns={INVENTORY_COLUMNS}
          rows={items}
          renderRow={(a) => (
            <tr key={a.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`frac-inventory-row-${a.id}`}>
              <Td className="font-medium text-slate-100">{a.asset_name}</Td>
              <Td className="text-slate-400">{a.description || a.asset_type || "—"}</Td>
              <Td className="text-slate-400">{a.owned_by || "—"}</Td>
              <Td><InvBadge status={a.status} testId={`frac-inventory-status-${a.id}`} /></Td>
              <Td className="text-slate-400 font-mono" data-testid={`frac-inventory-job-${a.id}`}>{a.job_number || "—"}</Td>
              <Td className="text-slate-400">{a.current_location || "—"}</Td>
              <Td>
                <button
                  data-testid={`frac-inventory-edit-${a.id}`}
                  onClick={() => openEdit(a)}
                  className="p-1.5 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-800 transition-colors duration-150"
                  title="Edit asset"
                >
                  <Pencil size={15} />
                </button>
              </Td>
            </tr>
          )}
        />
      )}

      <FormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title={`Edit ${editing?.asset_name || "Asset"}`} testId="frac-inventory-edit-dialog">
        <Field label="Tank/Asset #">
          <TextInput testId="frac-inventory-edit-name" value={form.asset_name || ""} onChange={(e) => setForm({ ...form, asset_name: e.target.value })} />
        </Field>
        <Field label="Type">
          <TextInput testId="frac-inventory-edit-type" value={form.asset_type || ""} onChange={(e) => setForm({ ...form, asset_type: e.target.value })} placeholder="Frac Tank, Pump, ..." />
        </Field>
        <Field label="Description">
          <TextInput testId="frac-inventory-edit-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label="Owned By">
          <TextInput testId="frac-inventory-edit-owned-by" value={form.owned_by || ""} onChange={(e) => setForm({ ...form, owned_by: e.target.value })} />
        </Field>
        <Field label="Current Location">
          <div data-testid="frac-inventory-edit-location" className="w-full bg-slate-900/40 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-400">
            {editing?.current_location || "—"}
          </div>
          <p className="text-xs text-amber-500/80 mt-1 font-mono uppercase tracking-wider">Must change on Dispatch board</p>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <ActionButton testId="frac-inventory-edit-cancel" variant="secondary" onClick={() => setEditing(null)}>Cancel</ActionButton>
          <ActionButton testId="frac-inventory-edit-save" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</ActionButton>
        </div>
      </FormDialog>
    </div>
  );
}
